import { useEffect } from 'react'
import {
    FaBrain,
    FaChartBar,
    FaChartLine,
    FaChevronDown,
    FaComments,
    FaDatabase,
    FaFilePdf,
    FaFileSignature,
    FaFolderOpen,
    FaGlobe,
    FaHistory,
    FaHome,
    FaInbox,
    FaPalette,
    FaPaperPlane,
    FaSearch,
    FaShieldAlt,
    FaSyncAlt,
    FaTasks,
    FaUniversity,
    FaUpload,
    FaUsers,
    FaUsersCog,
    FaVial,
    FaLayerGroup,
    FaListAlt,
    FaSignature,
    FaEnvelope,
} from 'react-icons/fa'
import { FaRobot } from 'react-icons/fa6'
import type { IconType } from 'react-icons'
import { RiLinksFill } from 'react-icons/ri'
import { MdManageSearch } from 'react-icons/md'
import { MdManageAccounts } from 'react-icons/md'
import { IoIosPersonAdd } from 'react-icons/io'

import { useIsPcpReferralTeamLeader } from '../authUserProvider'
import { MhlReviewBadge } from '../components/views/militaryHearingLossView/shared/MhlReviewBadge'
import {
    MhlScopedNavItem,
    type MhlScopeRequirement,
} from '../components/views/militaryHearingLossView/shared/MhlScopedNavItem'
import { NavItem, useSidebarNavigation } from '../components/layout/sidebar/NavItem'
import { usePageAccess } from '../access/pageAccess'
import { pageMeta } from '../access/pageMeta'
import styles from '../globals/sidebar.module.scss'

/** Icon for a page granted by an override, chosen from its catalogue section. */
const SECTION_ICONS: Record<string, IconType> = {
    General: FaHome,
    'Sentinel Claims': FaFolderOpen,
    'PCP Introducers': FaChartLine,
    'Introducer Stats': FaChartLine,
    'PCP Referral': FaChartLine,
    'Introducer APIs': FaVial,
    'Comms Management': FaComments,
    'Funding Data': FaDatabase,
    'Military Hearing Loss': FaUsers,
    'Re-Engagement': FaSearch,
    'Insurance Audit': FaShieldAlt,
    'Sentinel Tools': FaTasks,
    Admin: FaUsersCog,
}
const pageIcon = (path: string): IconType => SECTION_ICONS[pageMeta(path).section] || FaListAlt

export const SidebarDisplay = (organisation: string) => {
    const { isLeader: isTeamLeader } = useIsPcpReferralTeamLeader()
    // Same active-path rules <NavItem> uses, for the <details open> groups.
    const { isActive } = useSidebarNavigation()
    // Pages granted to this user's org(s) by an `allow` row in
    // dashboard_org_pages, appended as top-level links after the org's
    // hard-coded sidebar. Detail pages (with :params) are reachable from
    // their parent list, not linked directly.
    const { allowedPages } = usePageAccess()
    const extraPages = organisation === 'Sentinel' ? [] : allowedPages.filter((p) => !p.includes(':'))

    return (
        <ul className={styles.nav}>
            {organisation == 'Sentinel' && sentinelSidebar(isActive)}
            {organisation == 'Sensible' && reEngagementSidebar()}
            {organisation == 'ADL' && adlSidebar()}
            {organisation == 'MR' && mrSidebar()}
            {organisation == 'PCPMO26' && pcpmo26Sidebar()}
            {organisation == 'FairClaim' && fairclaimSidebar()}
            {organisation == 'SentinelMatters' && sentinelMattersSidebar()}
            {organisation == 'PCP Referral' && pcpReferralSidebar()}
            {organisation == 'PCP Referral Engagement' && pcpReferralEngagementSidebar(isTeamLeader)}
            {organisation == 'MBFCheck' && mbfcheckSidebar()}
            {organisation == 'MFC' && mfcSidebar()}
            {organisation == 'Vision Launch' && visionLaunchSidebar()}
            {organisation == 'Score' && scoreSidebar()}
            {organisation == 'Insurance Audit' && insuranceAuditSidebar()}
            {organisation == 'Military Hearing Loss' && militaryHearingLossSidebar()}
            {organisation == 'Military Hearing Loss Admin' && militaryHearingLossAdminSidebar()}

            {/* {organisation == 'Gorilla' && gorillaSidebar()}
            {organisation == 'Allegiant' && allegiantSidebar()}
            {organisation == 'Panaco' && panacoSidebar()} */}

            {extraPages.map((p) => (
                <NavItem key={p} path={p} label={pageMeta(p).name} Icon={pageIcon(p)} />
            ))}
        </ul>
    )
}

const sentinelSidebar = (isActive: (path: string) => boolean) => {
    return (
        <>
            <NavItem path="/home" label="Dashboard" Icon={FaHome} />
            {/* <NavItem path="/chatbot" label="Chatbot" Icon={FaRobot} /> */}
            <NavItem path="/client-search" label="Client Search" Icon={FaSearch} />

            <details
                className={styles.sidebarDropdown}
                open={
                    isActive('/cases') ||
                    isActive('/clients') ||
                    isActive('/media-claims') ||
                    isActive('/manual-add-client') ||
                    isActive('/needs-sorting-clients') ||
                    isActive('/loa-tracker')
                }
            >
                <summary>
                    <span className={styles.summaryContent}>
                        <FaFolderOpen />
                        Sentinel Claims
                    </span>
                    <FaChevronDown className={styles.chevron} />
                </summary>
                <ul className={styles.nestedStats}>
                    <NavItem path="/cases" label="Cases" Icon={FaFolderOpen} />
                    <NavItem path="/clients" label="Clients" Icon={FaUsers} />
                    <NavItem path="/media-claims" label="Media Claims" Icon={FaUsers} />
                    <NavItem path="/manual-add-client" label="Add Client" Icon={IoIosPersonAdd} />
                    <NavItem path="/needs-sorting-clients" label="Sort Manual Clients" Icon={FaInbox} />
                    <NavItem path="/loa-tracker" label="LOA Tracker" Icon={FaListAlt} />
                </ul>
            </details>

            <details className={styles.sidebarDropdown} open={isActive('/srds-home')}>
                <summary>
                    <span className={styles.summaryContent}>
                        <FaChartLine />
                        PCP Introducers
                    </span>
                    <FaChevronDown className={styles.chevron} />
                </summary>
                <ul className={styles.nestedStats}>
                    <NavItem path="/srds-home" label="SRDS" Icon={FaDatabase} />
                    <NavItem path="/sentinel-matters-home" label="SentinelMatters" Icon={FaChartLine} />
                    <NavItem path="/mfc-home" label="Evans Hughes" Icon={FaChartLine} />
                    <NavItem path="/fairclaim-home" label="Claim Delivery API" Icon={FaChartLine} />
                    <li>
                        <details className={styles.sidebarDropdown}>
                            <summary>
                                <span className={styles.summaryContent}>
                                    <FaChartLine />
                                    Referral
                                </span>
                                <FaChevronDown className={styles.chevron} />
                            </summary>
                            <ul className={styles.nestedStats}>
                                <NavItem path="/pcp-referral/overview" label="Overview" Icon={FaChartLine} />
                                <NavItem path="/pcp-referral/engagement" label="Engagement" Icon={FaSearch} />
                                <NavItem path="/pcp-referral/add-client" label="Add Client" Icon={IoIosPersonAdd} />
                                <NavItem path="/pcp-referral/engagement-stats" label="Stats" Icon={FaChartLine} />
                                <NavItem path="/pcp-referral/esign-history" label="eSign History" Icon={FaHistory} />
                                <NavItem path="/pcp-referral/users" label="Users" Icon={MdManageAccounts} />
                            </ul>
                        </details>
                    </li>
                    <li>
                        <details className={styles.sidebarDropdown}>
                            <summary>
                                <span className={styles.summaryContent}>
                                    <FaChartLine />
                                    Inactive
                                </span>
                                <FaChevronDown className={styles.chevron} />
                            </summary>
                            <ul className={styles.nestedStats}>
                                {/* <NavItem path="/sentinel-introducer-stats" label="Introducer" Icon={FaChartLine} /> */}
                                <NavItem path="/sentinel-home" label="Sentinel" Icon={FaChartLine} />
                                <NavItem path="/adl-home" label="ADL" Icon={FaChartLine} />
                                <NavItem path="/pcpmo26-home" label="PCPMO26" Icon={FaChartLine} />
                                <NavItem path="/mbfcheck-home" label="MBFCheck" Icon={FaChartLine} />
                                <NavItem path="/mail-referral-home" label="Check Finance" Icon={FaChartLine} />
                                {/* <NavItem path="/vision-launch-home" label="Vision Launch" Icon={FaChartLine} /> */}

                                {/* <NavItem path="/gorilla-home" label="Gorilla" Icon={FaChartLine} /> */}
                                {/* <NavItem path="/allegiant-home" label="Allegiant" Icon={FaChartLine} /> */}
                                {/* <NavItem path="/panaco-home" label="Panaco" Icon={FaChartLine} /> */}
                            </ul>
                        </details>
                    </li>
                </ul>
            </details>

            <details
                className={styles.sidebarDropdown}
                open={
                    isActive('/fairclaim-api') ||
                    isActive('/fairclaim-api/optics') ||
                    isActive('/fairclaim-api/linked-funding') ||
                    isActive('/fairclaim-api/test-suite') ||
                    isActive('/fairclaim-api/keys') ||
                    isActive('/ffj-api') ||
                    isActive('/ffj-api/optics') ||
                    isActive('/ffj-api/linked-funding') ||
                    isActive('/ffj-api/test-suite') ||
                    isActive('/ffj-api/keys')
                }
            >
                <summary>
                    <span className={styles.summaryContent}>
                        <FaVial />
                        Introducer APIs
                    </span>
                    <FaChevronDown className={styles.chevron} />
                </summary>
                <ul className={styles.nestedStats}>
                    <NavItem
                        path="/fairclaim-api"
                        label="Claim Delivery API"
                        Icon={FaVial}
                        activePaths={[
                            '/fairclaim-api/optics',
                            '/fairclaim-api/linked-funding',
                            '/fairclaim-api/test-suite',
                            '/fairclaim-api/keys',
                        ]}
                    />
                    <NavItem
                        path="/ffj-api"
                        label="FFJ API"
                        Icon={FaVial}
                        activePaths={[
                            '/ffj-api/optics',
                            '/ffj-api/linked-funding',
                            '/ffj-api/test-suite',
                            '/ffj-api/keys',
                        ]}
                    />
                </ul>
            </details>

            <details className={styles.sidebarDropdown} open={isActive('/comms')}>
                <summary>
                    <span className={styles.summaryContent}>
                        <FaComments />
                        Comms Management
                    </span>
                    <FaChevronDown className={styles.chevron} />
                </summary>
                <ul className={styles.nestedStats}>
                    <NavItem path="/comms/search" label="Search" Icon={FaSearch} />
                    <NavItem path="/comms/mailboxes" label="Mailboxes" Icon={FaSyncAlt} />
                    <NavItem path="/comms/pipeline" label="Pipeline" Icon={FaLayerGroup} />
                    <li>
                        <details
                            className={styles.sidebarDropdown}
                            {...(isActive('/comms/loc-batches') || isActive('/comms/document-ingest')
                                ? { open: true }
                                : {})}
                        >
                            <summary>
                                <span className={styles.summaryContent}>
                                    <FaPaperPlane />
                                    Letters
                                </span>
                                <FaChevronDown className={styles.chevron} />
                            </summary>
                            <ul className={styles.nestedStats}>
                                <NavItem path="/comms/loc-batches" label="LOC Batches" Icon={FaPaperPlane} />
                                <NavItem
                                    path="/comms/document-ingest"
                                    label="Daily Post (incoming)"
                                    Icon={FaInbox}
                                    title="Upload and file LOCs received in the post"
                                />
                            </ul>
                        </details>
                    </li>
                    <NavItem path="/comms/lenders" label="Lenders" Icon={FaUniversity} />
                    <NavItem path="/comms/categories" label="Categories & Rules" Icon={FaTasks} />
                    <NavItem path="/comms/models" label="AI Models" Icon={FaRobot} />
                </ul>
            </details>

            {/* Funding Data Dropdown */}
            <details className={styles.sidebarDropdown}>
                <summary>
                    <span className={styles.summaryContent}>
                        <FaChartBar />
                        Funding Data
                    </span>
                    <FaChevronDown className={styles.chevron} />
                </summary>
                <ul className={styles.nestedStats}>
                    <NavItem path="/collect-funding-clients" label="Collect Funding Clients" Icon={FaChartBar} />
                    <NavItem path="/allocate-claims" label="Allocate Claims" Icon={FaChartBar} />
                    {/* Allocate Claims (Full) hidden from sidebar — route still reachable directly */}
                    <NavItem path="/ineligible-funding-clients" label="Failed Audit" Icon={FaChartBar} />
                    <NavItem path="/introducer-funding-gap" label="non-fundable" Icon={FaChartBar} />
                    <NavItem path="/analyse-schema" label="Analyse Schema" Icon={FaChartBar} />
                    <NavItem path="/admin/approved-lenders" label="Approved Lenders" Icon={FaUniversity} />
                </ul>
            </details>

            {/* Military Hearing Loss Portal */}
            <details className={styles.sidebarDropdown} open={MHL_NAV_ITEMS.some((item) => isActive(item.path))}>
                <summary>
                    <span className={styles.summaryContent}>
                        <FaUsers />
                        Military Hearing Loss
                    </span>
                    <FaChevronDown className={styles.chevron} />
                </summary>
                <ul className={styles.nestedStats}>{mhlNavItems(true)}</ul>
            </details>

            {/* Irresponsible Lending hidden from sidebar — routes still reachable directly */}

            {/* <NavItem path="/knowledge-base" label="Knowledge Base" Icon={FaBrain} /> */}

            <details className={styles.sidebarDropdown}>
                <summary>
                    <span className={styles.summaryContent}>
                        <FaChartLine /> Re-Engagement
                    </span>
                    <FaChevronDown className={styles.chevron} />
                </summary>
                <ul className={styles.nestedStats}>
                    <NavItem path="/re-engagement-overview" label="Overview" Icon={FaChartLine} />
                    <NavItem path="/re-engagement-search" label="Client Search" Icon={FaChartLine} />
                </ul>
            </details>

            <details
                className={styles.sidebarDropdown}
                open={
                    isActive('/contracts') ||
                    isActive('/introducers-directory') ||
                    isActive('/cancellation-readiness') ||
                    isActive('/omnibus-claims') ||
                    isActive('/claim-valuations') ||
                    isActive('/report-links') ||
                    isActive('/viewthru-batch') ||
                    isActive('/admin/credit-scrapers') ||
                    isActive('/insurance-audit') ||
                    isActive('/signature-review') ||
                    isActive('/staff-email-signature')
                }
            >
                <summary>
                    <span className={styles.summaryContent}>
                        <FaTasks />
                        Sentinel Tools
                    </span>
                    <FaChevronDown className={styles.chevron} />
                </summary>
                <ul className={styles.nestedStats}>
                    <NavItem path="/omnibus-claims" label="Omnibus Claims" Icon={FaLayerGroup} />
                    <NavItem path="/viewthru-batch" label="ViewThru Batch" Icon={FaUpload} />
                    <NavItem path="/contracts" label="Contracts" Icon={FaFileSignature} />
                    <NavItem path="/signature-review" label="Signature Review" Icon={FaSignature} />
                    <NavItem path="/introducers-directory" label="Introducers Directory" Icon={RiLinksFill} />
                    <NavItem path="/cancellation-readiness" label="Cancellation Readiness" Icon={FaShieldAlt} />
                    <NavItem path="/insurance-audit" label="Insurance Audit" Icon={FaShieldAlt} />
                    <NavItem path="/staff-email-signature" label="Staff Email Signature" Icon={FaEnvelope} />
                    <NavItem path="/admin/credit-scrapers" label="Credit Scrapers" Icon={FaSyncAlt} />
                    <NavItem path="/claim-valuations" label="Claim Valuations" Icon={FaChartBar} />
                    <NavItem path="/report-links" label="Report Builder" Icon={RiLinksFill} />
                </ul>
            </details>

            {/* Admin Dropdown — Sentinel admins only. The underlying pages
                still gate themselves via `isAdminUser`, this just hides the
                links from non-admin users in the sidebar UI. */}
            <details
                className={styles.sidebarDropdown}
                open={
                    isActive('/users') ||
                    isActive('/admin/user-activity') ||
                    isActive('/admin/insurance-audit') ||
                    isActive('/admin/batch-visibility') ||
                    isActive('/admin/omnibus-comms-upload') ||
                    isActive('/admin/document-access') ||
                    isActive('/admin/style-guide')
                }
            >
                <summary>
                    <span className={styles.summaryContent}>
                        <FaUsersCog />
                        Admin
                    </span>
                    <FaChevronDown className={styles.chevron} />
                </summary>
                <ul className={styles.nestedStats}>
                    <NavItem path="/users" label="Users" Icon={FaUsersCog} />
                    <NavItem path="/admin/user-activity" label="User Activity" Icon={FaHistory} />
                    <NavItem path="/admin/insurance-audit" label="Insurance Audit Admin" Icon={FaUsersCog} />
                    <NavItem path="/admin/batch-visibility" label="Omnibus Batch Visibility" Icon={FaLayerGroup} />
                    <NavItem path="/admin/omnibus-comms-upload" label="Omnibus Comms Upload" Icon={FaEnvelope} />
                    <NavItem path="/admin/document-access" label="Bundle Access Log" Icon={FaFilePdf} />
                    <NavItem path="/admin/style-guide" label="Style Guide" Icon={FaPalette} />
                </ul>
            </details>
        </>
    )
}

const adlSidebar = () => {
    return (
        <>
            <NavItem path="/adl-home" label="ADL Stats" Icon={FaChartLine} />
            <NavItem path="/adl-il-stats" label="ADL IL Stats" Icon={FaChartLine} />
        </>
    )
}

const mrSidebar = () => {
    return (
        <>
            <NavItem path="/mail-referral-home" label="Mail Stats" Icon={FaChartLine} />
            <NavItem path="/mail-il-stats" label="Mail IL Stats" Icon={FaChartLine} />
        </>
    )
}

const pcpmo26Sidebar = () => {
    return (
        <>
            <NavItem path="/pcpmo26-home" label="PCPMO26 Stats" Icon={FaChartLine} />
        </>
    )
}

const fairclaimSidebar = () => {
    return (
        <>
            <NavItem path="/fairclaim-home" label="Claim Delivery API Stats" Icon={FaChartLine} />
        </>
    )
}

const sentinelMattersSidebar = () => {
    return (
        <>
            <NavItem path="/sentinel-matters-home" label="SentinelMatters Stats" Icon={FaChartLine} />
        </>
    )
}

const pcpReferralSidebar = () => {
    return (
        <>
            <details className={styles.sidebarDropdown} open>
                <summary>
                    <span className={styles.summaryContent}>
                        <FaChartLine />
                        PCP Referral
                    </span>
                    <FaChevronDown className={styles.chevron} />
                </summary>
                <ul className={styles.nestedStats}>
                    <NavItem path="/pcp-referral/overview" label="Overview" Icon={FaChartLine} />
                    <NavItem path="/pcp-referral/engagement" label="Engagement" Icon={FaSearch} />
                    <NavItem path="/pcp-referral/add-client" label="Add Client" Icon={IoIosPersonAdd} />
                    <NavItem path="/pcp-referral/engagement-stats" label="Stats" Icon={FaChartLine} />
                    <NavItem path="/pcp-referral/esign-history" label="eSign History" Icon={FaHistory} />
                    <NavItem path="/pcp-referral/users" label="Users" Icon={MdManageAccounts} />
                </ul>
            </details>
        </>
    )
}

const pcpReferralEngagementSidebar = (isTeamLeader = false) => {
    return (
        <>
            <NavItem path="/pcp-referral/engagement" label="Engagement" Icon={FaSearch} />
            <NavItem path="/pcp-referral/add-client" label="Add Client" Icon={IoIosPersonAdd} />
            {isTeamLeader && <NavItem path="/pcp-referral/team-stats" label="My Team" Icon={FaChartLine} />}
        </>
    )
}

const mbfcheckSidebar = () => {
    return (
        <>
            <NavItem path="/mbfcheck-home" label="MBFCheck Stats" Icon={FaChartLine} />
        </>
    )
}

const mfcSidebar = () => {
    return (
        <>
            <NavItem path="/mfc-home" label="Evans Hughes" Icon={FaChartLine} />
        </>
    )
}

const panacoSidebar = () => {
    return (
        <>
            <NavItem path="/panaco-home" label="Panaco Stats" Icon={FaChartLine} />
        </>
    )
}

const gorillaSidebar = () => {
    return (
        <>
            <NavItem path="/gorilla-home" label="Gorilla Stats" Icon={FaChartLine} />
        </>
    )
}

const reEngagementSidebar = () => {
    return (
        <>
            <details className={styles.sidebarDropdown}>
                <summary>
                    <span className={styles.summaryContent}>
                        <FaChartLine /> Re-Engagement
                    </span>
                    <FaChevronDown className={styles.chevron} />
                </summary>
                <ul className={styles.nestedStats}>
                    <NavItem path="/re-engagement-overview" label="Overview" Icon={FaChartLine} />
                    <NavItem path="/re-engagement-search" label="Client Search" Icon={FaChartLine} />
                </ul>
            </details>
        </>
    )
}

const visionLaunchSidebar = () => {
    return (
        <>
            <NavItem path="/vision-launch-home" label="Vision Launch Stats" Icon={FaChartLine} />
        </>
    )
}

const scoreSidebar = () => {
    return (
        <>
            <NavItem path="/allocate-claims" label="Allocate Claims" Icon={FaChartBar} />
            {/* Allocate Claims (Full) hidden from sidebar — route still reachable directly */}
            <NavItem path="/introducer-funding-gap" label="Not in funding batches (introducers)" Icon={FaChartBar} />
        </>
    )
}

const insuranceAuditSidebar = () => {
    return (
        <>
            <NavItem path="/insurance-audit" label="Audit Cases" Icon={FaShieldAlt} />
        </>
    )
}

const allegiantSidebar = () => {
    return (
        <>
            <NavItem path="/allegiant-home" label="Allegiant Stats" Icon={FaChartLine} />
        </>
    )
}

/**
 * Military Hearing Loss nav items, in display order. Admin-only pages are
 * flagged so the agent variant can drop them. Shared by the agent, admin and
 * Sentinel sidebars so order and labels never drift.
 */
const MHL_NAV_ITEMS: {
    path: string
    label: string
    Icon: IconType
    adminOnly?: boolean
    /** Shown to agents only when their server scope has this flag (team leaders). */
    requires?: MhlScopeRequirement
    badge?: boolean
}[] = [
    { path: '/military-hearing-loss/lead-tracker', label: 'Overview', Icon: FaChartLine, requires: 'canViewStats' },
    { path: '/military-hearing-loss/leads', label: 'Clients', Icon: FaSearch, badge: true },
    { path: '/military-hearing-loss/add-client', label: 'Add Client', Icon: FaInbox },
    { path: '/military-hearing-loss/admin', label: 'Agent Stats', Icon: FaChartBar, requires: 'canViewStats' },
    {
        path: '/military-hearing-loss/website-visitors',
        label: 'Website Visitors',
        Icon: FaGlobe,
        requires: 'canViewStats',
    },
    { path: '/military-hearing-loss/users', label: 'Agents', Icon: FaUsers, requires: 'isTeamLeader' },
]

const mhlNavItems = (admin: boolean) =>
    MHL_NAV_ITEMS.filter((item) => admin || !item.adminOnly).map(({ path, label, Icon, badge, requires }) =>
        !admin && requires ? (
            <MhlScopedNavItem key={path} path={path} label={label} Icon={Icon} requires={requires} />
        ) : (
            <NavItem
                key={path}
                path={path}
                label={label}
                Icon={Icon}
                badge={
                    badge ? (
                        <>
                            {' '}
                            <MhlReviewBadge />
                        </>
                    ) : undefined
                }
            />
        ),
    )

const militaryHearingLossSidebar = () => <>{mhlNavItems(false)}</>

const militaryHearingLossAdminSidebar = () => <>{mhlNavItems(true)}</>
